import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const BusinessProductservice = () => {
  const [productOrServices, setProductOrServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({ name: '', businessCategory: '', businessSubCategory: '' });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch product/services and categories on page or debounced search change
  useEffect(() => {
    fetchProductOrServices(currentPage, debouncedSearchQuery);
    fetchCategories();
  }, [currentPage, debouncedSearchQuery]);

  // Fetch subcategories whenever selected businessCategory changes
  useEffect(() => {
    if (formValues.category) {
      fetchSubcategories(formValues.category);
    } else {
      setSubcategories([]);
      setFormValues((prev) => ({ ...prev, subCategory: '' }));
    }
  }, [formValues.category]);
  
  const fetchProductOrServices = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/productOrService?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      const { productOrServices = [], totalCount = 0 } = res.data;
      setProductOrServices(productOrServices);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Product & Service:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/admin/api/businessCategory');
      setCategories(res.data.businessCategories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

 const fetchSubcategories = async (categoryId) => {
  try {
    const res = await axios.get(`/admin/api/businessSubCategory?categoryId=${categoryId}`);
    setSubcategories(res.data.businessSubCategories || []);
  } catch (err) {
    console.error('Failed to fetch subcategories:', err);
  }
};

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      category: { required: true },
      subCategory: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/admin/api/productOrService/${editingId}`, formValues);
        toastSuccess('Product & Service updated successfully!');
      } else {
        await axios.post('/admin/api/productOrService', formValues);
        toastSuccess('Product & Service added successfully!');
      }
      setCurrentPage(1);
      fetchProductOrServices(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Product & Service:', err);
      toastError('Failed to save Product & Service');
    }
  };

  const handleEdit = async (data) => {
    setFormValues({
      name: data.name || '',
      category: data.category?._id || '',
      subCategory: data.subCategory?._id || '',
    });

    if (data.category?._id) {
      await fetchSubcategories(data.category._id);
    } else {
      setSubcategories([]);
    }

    setEditingId(data._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete('Are you sure you want to delete this product/service?');
    if (confirmed) {
      try {
        await axios.delete(`/admin/api/productOrService/${id}`);
        toastSuccess('Product & Service deleted successfully!');
        setCurrentPage(1);
        fetchProductOrServices(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Product & Service:', err);
        toastError('Failed to delete Product & Service');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', category: '', subCategory: '' });
    setEditingId(null);
    setErrors({});
  };

  // Dropdown options for categories and subcategories
  const categoryOptions = categories.map(cat => ({ label: cat.name, value: cat._id }));
  const subcategoryOptions = subcategories.map(sub => ({ label: sub.name, value: sub._id }));

  // Fields for CommonModalForm (supports select)
  const fields = [
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: categoryOptions,
    },
    {
      name: 'subCategory',
      label: 'Subcategory',
      type: 'select',
      options: subcategoryOptions,
      isMulti: true,
    },
    { name: 'name', label: 'Product&Service',placeholder: 'Enter Product/Service Name' },

  ];

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Product & Service</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Product & Service (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Product & Service List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New Product & Service
          </button>
        </div>
      <div className="table-responsive">
        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr No.</th>
                <th className="py-1 px-2">Name</th>
                <th className="py-1 px-2">Category</th>
                <th className="py-1 px-2">Subcategory</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {productOrServices.length > 0 ? (
                productOrServices.map((item, idx) => (
                  <tr key={item._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">{item.name}</td>
                    <td className="py-1 px-2">{item.category?.name || '-'}</td>
                    <td className="py-1 px-2">{Array.isArray(item.subCategory)? item.subCategory.map(sub => sub?.name).filter(Boolean).join(', ') || '-': (item.subCategory?.name || '-')}</td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(item)}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                        onClick={() => handleDelete(item._id)}
                        title="Delete"
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-2">No Product & Service found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>&laquo;</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className="{`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button className="btn btn-sm btn-outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>&raquo;</button>
            </div>
          )}
        </div>
      </div>
    </div>

      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Product & Service' : 'Add Product & Service'}
        fields={fields}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={['name','category','subCategory']}
      />
    </div>
  );
};

export default BusinessProductservice;
